import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

// Auto-advancing carousel. Supports two slide types:
//  - image-only:  { image, alt }        -> shows the full designed graphic
//  - testimonial: { image, quote, name } -> shows a quote card with small avatar
export default function Carousel({ slides = [], interval = 5000 }) {
  const [index, setIndex] = useState(0);
  const timer = useRef(null);
  const count = slides.length;

  const go = useCallback((next) => setIndex((prev) => (next + count) % count), [count]);

  const restart = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, interval);
  }, [count, interval]);

  useEffect(() => {
    if (count <= 1) return;
    restart();
    return () => timer.current && clearInterval(timer.current);
  }, [count, restart]);

  if (count === 0) return null;

  return (
    <div className="relative w-full max-w-lg mx-auto">
      <div className="overflow-hidden rounded-2xl">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((s, i) => (
            <div key={i} className="min-w-full">
              {s.quote ? (
                <div className="mx-auto bg-white border border-neutral-100 rounded-2xl px-8 py-10 flex flex-col items-center text-center">
                  {s.image && (
                    <img
                      src={s.image}
                      alt={s.name || "Client"}
                      className="w-20 h-20 rounded-full object-cover ring-2 ring-[var(--teal)] mb-5"
                    />
                  )}
                  <Quote className="text-[var(--teal)] mb-4" size={26} />
                  <p className="text-neutral-600 italic leading-relaxed text-[0.95rem] max-w-xl">
                    {s.quote}
                  </p>
                  {s.name && (
                    <p className="mt-5 text-purple font-semibold tracking-wide-sm uppercase text-sm">
                      {s.name}
                    </p>
                  )}
                </div>
              ) : (
                <img
                  src={s.image}
                  alt={s.alt || "Slide"}
                  className="w-full h-auto block rounded-2xl"
                  loading="lazy"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {count > 1 && (
        <>
          <button
            onClick={() => {
              go(index - 1);
              restart();
            }}
            aria-label="Previous slide"
            className="absolute left-2 md:-left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center text-[var(--teal-dark)] hover:bg-white hover:scale-105 transition-all"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => {
              go(index + 1);
              restart();
            }}
            aria-label="Next slide"
            className="absolute right-2 md:-right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow-md flex items-center justify-center text-[var(--teal-dark)] hover:bg-white hover:scale-105 transition-all"
          >
            <ChevronRight size={20} />
          </button>

          <div className="flex justify-center gap-2 mt-6">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setIndex(i);
                  restart();
                }}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  i === index ? "w-8 bg-[var(--teal)]" : "w-2.5 bg-neutral-300 hover:bg-neutral-400"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
